import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';

import {
  billingListWriteOffs,
  billingPostWriteOff,
  billingReverseWriteOff,
} from '../../services/api/index';
import type {
  ActiveBillingCycleItem,
  WriteOffListResponse,
} from '../../services/api/types.gen';
import { billingKeys } from '../../services/queryClient';
import { PermissionSlug } from '../../constants/featureFlags';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { formatMoneyDisplay } from '../../utils/currencyUtils';
import {
  formatDateForDisplay,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import { useToast } from '../../hooks/useToastContext';
import { useStore } from '../../store';
import { formatRemittanceCurrency } from './remittance/remittanceFormatters';

const REASON_MAX_LENGTH = 200;

/**
 * Write off part or all of a claim's open balance (EMR-2718).
 *
 * A write-off is an append-only dated row; a mistake is reversed with a new
 * row, never edited. The server dates the row and re-checks the balance and
 * the `billing:view` gate, so this component only hides the button.
 */
export const WriteOffAction: React.FC<{
  billingCycleId: string;
  claim: Pick<
    ActiveBillingCycleItem,
    | 'patient_name'
    | 'patient_mrn'
    | 'claim_number'
    | 'insurance_name'
    | 'billing_period_start'
    | 'billing_period_end'
    | 'total_charges'
    | 'total_payment'
  >;
}> = ({ billingCycleId, claim }) => {
  const canWriteOff = useStore(state =>
    state.hasPermission(PermissionSlug.BILLING_ACCESS)
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState<WriteOffListResponse | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // One id per dialog open (and per reversal), reused when a submit is retried.
  const [postId, setPostId] = useState('');
  const [reversing, setReversing] = useState<{
    id: string;
    clientId: string;
  } | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  const load = useCallback(async () => {
    try {
      const result = await billingListWriteOffs({
        path: { billing_cycle_id: billingCycleId },
      });
      if (result.error) throw result;
      setData(result.data ?? null);
      setAmount(result.data?.open_balance ?? '');
    } catch (error) {
      console.error('Failed to load write-offs:', error);
      showError(
        'Unable to load write-offs',
        getBusinessErrorMessage(error) ?? 'Please try again.'
      );
    }
  }, [billingCycleId, showError]);

  if (!canWriteOff) return null;

  const openDialog = () => {
    setData(null);
    setReason('');
    setReverseReason('');
    setReversing(null);
    setPostId(crypto.randomUUID());
    setOpen(true);
    void load();
  };

  const refreshLists = async () => {
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: billingKeys.billingCycles(),
        }),
        queryClient.invalidateQueries({ queryKey: billingKeys.billingMeta() }),
      ]);
    } catch (error) {
      console.error('Failed to refresh after a write-off:', error);
    }
  };

  const run = async (
    call: () => Promise<{ error?: unknown }>,
    failure: string
  ): Promise<boolean> => {
    setBusy(true);
    try {
      const result = await call();
      // The whole result: getBusinessErrorMessage reads the status from it.
      if (result.error) throw result;
      return true;
    } catch (error) {
      console.error(`${failure}:`, error);
      showError(failure, getBusinessErrorMessage(error) ?? 'Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const ok = await run(
      () =>
        billingPostWriteOff({
          path: { billing_cycle_id: billingCycleId },
          body: { id: postId, amount: amount.trim(), reason: reason.trim() },
        }),
      'Unable to write off'
    );
    if (!ok) return;
    showSuccess('Write-off posted');
    setOpen(false);
    await refreshLists();
  };

  const reverse = async () => {
    if (!reversing) return;
    const ok = await run(
      () =>
        billingReverseWriteOff({
          path: { write_off_id: reversing.id },
          body: { id: reversing.clientId, reason: reverseReason.trim() },
        }),
      'Unable to reverse'
    );
    if (!ok) return;
    showSuccess('Write-off reversed');
    setReversing(null);
    setReverseReason('');
    await Promise.all([load(), refreshLists()]);
  };

  const amountNumber = Number(amount);
  const balance = data?.open_balance ?? null;
  const canSubmit =
    !busy && balance !== null && reason.trim().length > 0 && amountNumber > 0;

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              onClick={openDialog}
              className='text-muted-foreground hover:text-foreground'
              aria-label='Write off'
            >
              <Landmark className='h-4 w-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='bottom' className='text-xs'>
            Write off
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen && !busy) setOpen(false);
        }}
      >
        <DialogContent className='sm:max-w-[520px]'>
          <DialogHeader>
            <DialogTitle>Write off balance</DialogTitle>
            <DialogDescription>
              Posts today
              {data ? ` as ${formatDateForDisplay(data.posting_date)}` : ''}.
              Shows in AR reports after the next refresh.
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-col gap-3 py-2'>
            <dl className='grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md bg-muted/50 p-3 text-sm'>
              {[
                ['Patient', claim.patient_name],
                ['MRN', claim.patient_mrn],
                ['Claim #', claim.claim_number],
                ['Payer', claim.insurance_name],
                [
                  'Billing period',
                  `${formatDateToMMDDYYYY(claim.billing_period_start)} - ${formatDateToMMDDYYYY(claim.billing_period_end)}`,
                ],
                ['Total charges', formatMoneyDisplay(claim.total_charges)],
                ['Total paid', formatMoneyDisplay(claim.total_payment) || '-'],
              ].map(([label, value]) => (
                <React.Fragment key={label}>
                  <dt className='text-muted-foreground'>{label}</dt>
                  <dd className='text-right font-medium'>{value}</dd>
                </React.Fragment>
              ))}
            </dl>
            <div className='flex justify-between text-sm'>
              <span>Open balance</span>
              <span className='font-medium'>
                {data ? formatRemittanceCurrency(data.open_balance) : '...'}
              </span>
            </div>
            <div className='flex justify-between text-sm'>
              <span>Net written off</span>
              <span className='font-medium'>
                {data ? formatRemittanceCurrency(data.net_written_off) : '...'}
              </span>
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='write-off-amount'>Amount</Label>
              <Input
                id='write-off-amount'
                inputMode='decimal'
                inputSize='sm'
                value={amount}
                onChange={event => setAmount(event.target.value)}
                disabled={busy || balance === null}
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='write-off-reason'>Reason</Label>
              <Input
                id='write-off-reason'
                inputSize='sm'
                value={reason}
                maxLength={REASON_MAX_LENGTH}
                onChange={event => setReason(event.target.value)}
                disabled={busy}
              />
              <span className='text-xs text-muted-foreground'>
                Do not enter patient information.
              </span>
            </div>
            {data && data.rows.length > 0 && (
              <div className='flex flex-col gap-2 border-t pt-3'>
                <span className='text-sm font-medium'>History</span>
                {data.rows.map(row => (
                  <div key={row.id} className='flex flex-col gap-1 text-sm'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <span>
                        {formatDateForDisplay(row.posted_date)}{' '}
                        {formatRemittanceCurrency(row.amount)}{' '}
                        {row.posted_by_name ?? ''}
                      </span>
                      {!row.reversed && !row.reverses_id && (
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          disabled={busy}
                          onClick={() => {
                            setReversing({
                              id: row.id,
                              clientId: crypto.randomUUID(),
                            });
                            setReverseReason('');
                          }}
                        >
                          Reverse
                        </Button>
                      )}
                    </div>
                    <span className='text-muted-foreground'>
                      {row.reason}
                      {row.reversed ? ' (reversed)' : ''}
                    </span>
                    {reversing?.id === row.id && (
                      <div className='flex flex-col gap-1.5'>
                        <Label htmlFor='write-off-reverse-reason'>
                          Reason for reversal
                        </Label>
                        <Input
                          id='write-off-reverse-reason'
                          inputSize='sm'
                          value={reverseReason}
                          maxLength={REASON_MAX_LENGTH}
                          onChange={event =>
                            setReverseReason(event.target.value)
                          }
                          disabled={busy}
                        />
                        <Button
                          type='button'
                          size='sm'
                          disabled={busy || reverseReason.trim().length === 0}
                          onClick={() => void reverse()}
                        >
                          Confirm reversal
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              disabled={busy}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              Write off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WriteOffAction;
